import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { User } from '../api/auth';
import {
  getEnvSettings,
  updateEnvSettings,
  migrateEnvSettings,
  EnvSetting,
} from '../api';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';

interface AdminEnvTabProps {
  currentUser: User;
  admins: (User & { is_active?: boolean; created_at?: string })[];
}

const AdminEnvTab: React.FC<AdminEnvTabProps> = ({ currentUser, admins }) => {
  const [envSettings, setEnvSettings] = useState<Record<number, Record<string, EnvSetting>>>({});
  const [envKeys, setEnvKeys] = useState<string[]>([]);
  const [selectedEnvAdminId, setSelectedEnvAdminId] = useState<number | null>(null);
  const [editingEnvSettings, setEditingEnvSettings] = useState<Record<string, string>>({});
  const [envLoading, setEnvLoading] = useState(false);
  const [migrateEnvFile, setMigrateEnvFile] = useState('');

  useEffect(() => {
    loadEnvSettings();
  }, []);

  const loadEnvSettings = async () => {
    setEnvLoading(true);
    try {
      const data = await getEnvSettings();
      setEnvSettings(data.settings || {});
      setEnvKeys(data.env_keys || []);
    } catch (error) {
      console.error('환경설정 조회 실패:', error);
      toast.error('환경변수 설정을 불러오는데 실패했음.');
    } finally {
      setEnvLoading(false);
    }
  };

  const handleSelectEnvAdmin = (adminId: number) => {
    setSelectedEnvAdminId(adminId);
    const adminSettings = envSettings[adminId] || {};
    const editValues: Record<string, string> = {};
    envKeys.forEach(key => {
      const setting = adminSettings[key];
      const isSensitiveKey = key.includes('PASSWORD') || key.includes('TOKEN') || key.includes('API_KEY');
      editValues[key] = isSensitiveKey ? '' : (setting?.value || '');
    });
    setEditingEnvSettings(editValues);
  };

  const handleSaveEnvSettings = async () => {
    if (!selectedEnvAdminId) {
      toast.error('관리자를 선택해.');
      return;
    }

    const filteredSettings: Record<string, string> = {};
    Object.entries(editingEnvSettings).forEach(([key, value]) => {
      if (value && value.trim()) {
        filteredSettings[key] = value.trim();
      }
    });

    if (Object.keys(filteredSettings).length === 0) {
      toast.warning('저장할 설정이 없음.');
      return;
    }

    setEnvLoading(true);
    try {
      const result = await updateEnvSettings(selectedEnvAdminId, filteredSettings);
      toast.success(`환경변수가 저장되었음. (${result.updated.join(', ')})`);
      await loadEnvSettings();
      handleSelectEnvAdmin(selectedEnvAdminId);
    } catch (error: any) {
      console.error('환경설정 저장 실패:', error);
      toast.error(`환경변수 저장 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEnvLoading(false);
    }
  };

  const handleMigrateEnv = async () => {
    if (!migrateEnvFile || !selectedEnvAdminId) {
      toast.error('환경 파일명과 관리자를 선택해.');
      return;
    }

    setEnvLoading(true);
    try {
      const result = await migrateEnvSettings(migrateEnvFile, selectedEnvAdminId);
      toast.success(`환경변수 마이그레이션 완료! (${result.migrated.join(', ')})`);
      await loadEnvSettings();
      handleSelectEnvAdmin(selectedEnvAdminId);
      setMigrateEnvFile('');
    } catch (error: any) {
      console.error('환경설정 마이그레이션 실패:', error);
      toast.error(`마이그레이션 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEnvLoading(false);
    }
  };

  return (
    <div style={styles.envContainer}>
      {/* 관리자 선택 및 마이그레이션 섹션 */}
      <div style={styles.envSection}>
        <h4 style={styles.envSectionTitle}>환경변수 관리</h4>
        <p style={styles.envDescription}>
          관리자별 Notion, Psynet, Nate 등의 환경변수를 DB에서 관리함.
        </p>
        <div style={styles.envFormRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>관리자 선택</label>
            <select
              style={styles.select}
              value={selectedEnvAdminId || ''}
              onChange={(e) => handleSelectEnvAdmin(Number(e.target.value))}
            >
              <option value="">-- 관리자 선택 --</option>
              {admins.map(admin => (
                <option key={admin.admin_id} value={admin.admin_id}>
                  {admin.username} ({admin.role})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>.env 파일에서 마이그레이션</label>
            <div style={{ display: 'flex', gap: spacing[3] }}>
              <input
                type="text"
                style={styles.input}
                placeholder="파일명 (예: kim, endgame)"
                value={migrateEnvFile}
                onChange={(e) => setMigrateEnvFile(e.target.value)}
              />
              <button
                onClick={handleMigrateEnv}
                disabled={!migrateEnvFile || !selectedEnvAdminId || envLoading}
                style={styles.editButton}
              >
                마이그레이션
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 환경변수 편집 폼 */}
      {selectedEnvAdminId && (
        <div style={styles.envSection}>
          <h4 style={styles.envSectionTitle}>
            {admins.find(a => a.admin_id === selectedEnvAdminId)?.username} 환경변수 설정
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
            {envKeys.map(key => {
              const currentSetting = envSettings[selectedEnvAdminId]?.[key];
              const isSensitive = key.includes('PASSWORD') || key.includes('TOKEN') || key.includes('API_KEY');
              return (
                <div key={key} style={styles.envInputRow}>
                  <label style={styles.envLabel}>
                    {key}
                    {isSensitive && <span style={styles.encryptedBadge}>민감정보</span>}
                  </label>
                  <div style={styles.envInputContainer}>
                    <input
                      type={isSensitive ? 'password' : 'text'}
                      style={styles.envInput}
                      value={editingEnvSettings[key] || ''}
                      onChange={(e) => setEditingEnvSettings(prev => ({
                        ...prev,
                        [key]: e.target.value
                      }))}
                      placeholder={currentSetting?.value === '********' ? '새 값 입력 (변경 시에만)' : '값을 입력해야 함'}
                    />
                    {currentSetting && (
                      <span style={styles.envStatus}>
                        {currentSetting.value === '********' ? '설정됨' : (currentSetting.value ? '설정됨' : '미설정')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: spacing[6], display: 'flex', gap: spacing[4] }}>
            <button
              onClick={handleSaveEnvSettings}
              disabled={envLoading}
              style={styles.addButton}
            >
              {envLoading ? '저장 중...' : '저장'}
            </button>
            <button
              onClick={() => handleSelectEnvAdmin(selectedEnvAdminId)}
              disabled={envLoading}
              style={styles.editButton}
            >
              초기화
            </button>
          </div>
        </div>
      )}

      {/* 전체 환경변수 현황 테이블 */}
      <div style={styles.envSection}>
        <h4 style={styles.envSectionTitle}>관리자별 환경변수 현황</h4>
        {envLoading ? (
          <div style={styles.emptyState}>환경변수를 불러오는 중...</div>
        ) : Object.keys(envSettings).length === 0 ? (
          <div style={styles.emptyState}>등록된 환경변수가 없음.</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>관리자</th>
                  {envKeys.map(key => (
                    <th key={key} style={styles.th}>{key.replace('_', ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {admins.map(admin => {
                  const adminSettings = envSettings[admin.admin_id] || {};
                  return (
                    <tr key={admin.admin_id}>
                      <td style={styles.td}>
                        <strong>{admin.username}</strong>
                        <br />
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.gray[500] }}>{admin.role}</span>
                      </td>
                      {envKeys.map(key => {
                        const setting = adminSettings[key];
                        const isSensitiveKey = key.includes('PASSWORD') || key.includes('TOKEN') || key.includes('API_KEY');
                        return (
                          <td key={key} style={styles.td}>
                            {setting ? (
                              <span style={{ color: colors.status.success }}>
                                {isSensitiveKey ? '***' : (setting.value?.substring(0, 10) + (setting.value?.length > 10 ? '...' : ''))}
                              </span>
                            ) : (
                              <span style={{ color: colors.gray[400] }}>
                                미설정
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  envContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[5],
  },
  envSection: {
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
    padding: spacing[6],
  },
  envSectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginTop: 0,
    marginBottom: spacing[5],
  },
  envDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    marginBottom: spacing[5],
    lineHeight: typography.lineHeight.relaxed,
  },
  envFormRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[5],
    alignItems: 'flex-end',
  },
  envInputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
  },
  envLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[700],
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
  },
  encryptedBadge: {
    fontSize: typography.fontSize.xs,
    color: colors.status.warning,
    backgroundColor: colors.gray[100],
    padding: `${spacing[1]} ${spacing[2]}`,
    borderRadius: '4px',
  },
  envInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[3],
  },
  envInput: {
    ...commonStyles.input,
    flex: 1,
    maxWidth: '400px',
  },
  envStatus: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[500],
    minWidth: '60px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  label: {
    ...commonStyles.label,
    minWidth: 'auto',
  },
  input: {
    ...commonStyles.input,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    ...commonStyles.select,
    width: '100%',
  },
  addButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
  editButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  tableContainer: {
    ...commonStyles.tableContainer,
  },
  table: {
    ...commonStyles.table,
  },
  th: {
    ...commonStyles.th,
  },
  td: {
    ...commonStyles.td,
  },
  emptyState: {
    ...commonStyles.emptyState,
  },
};

export default AdminEnvTab;
